/**
 * YearlySummaryCard - Resumo Acumulado por SAFRA do Calendário de Sangria
 * 
 * Exibe totais acumulados baseado no período da safra configurado:
 * - Safra atual (ex: Set/2025 a Ago/2026)
 * - Comparativo com safra anterior
 * - Métricas: Chuva, Produção, Dias de Sangria, Reposições, Ethrel
 * 
 * Dependências:
 * - useTranslation (traduções)
 * - axios (requisições API)
 * - Card components (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { useTranslation } from "../translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Info icon component - ícone de informação intuitivo
const HelpIcon = ({ tooltip }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button className="ml-1 inline-flex items-center justify-center w-5 h-5 text-green-600 hover:text-green-800 transition-colors cursor-help">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
          </svg>
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
        <p className="text-sm leading-relaxed">{tooltip}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const YearlySummaryCard = ({ propertyId, currentMonth }) => {
  const { t } = useTranslation();
  const [harvestData, setHarvestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  // Dados opcionais de equipe/alocação (vêm do team-report-v2 do mês corrente).
  // Não bloqueiam o card: se a chamada falhar, a UI segue normal.
  const [teamInfo, setTeamInfo] = useState(null);

  useEffect(() => {
    if (!propertyId) return;

    const fetchHarvestSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/bleeding-records/${propertyId}/harvest-summary`);
        setHarvestData(response.data);
      } catch (err) {
        console.error("Error fetching harvest summary:", err);
        setError(err.response?.data?.detail || "Erro ao carregar resumo da safra");
      } finally {
        setLoading(false);
      }
    };

    fetchHarvestSummary();
  }, [propertyId, currentMonth]);

  // Fetch opcional do team-report-v2 (alocação por sangrador + plantas ativas/paralisadas no mês)
  // Independente do harvest-summary: se falhar, simplesmente não mostra a pill de equipe.
  useEffect(() => {
    if (!propertyId || !currentMonth) { setTeamInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/bleeding-records/${propertyId}/team-report-v2`,
          { params: { month: currentMonth } }
        );
        if (!cancelled) setTeamInfo(r.data || null);
      } catch {
        if (!cancelled) setTeamInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [propertyId, currentMonth]);

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
            <span className="ml-2 text-green-600">Carregando resumo da safra...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="bg-red-50 border-red-200">
        <CardContent className="py-4">
          <p className="text-red-600 text-center">❌ {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!harvestData) return null;

  const { current_harvest, previous_harvest, comparison, harvest_config } = harvestData;
  const currentSummary = current_harvest?.summary || {};
  const previousSummary = previous_harvest?.summary || {};
  
  const monthNames = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  // Helper to render change indicator
  const ChangeIndicator = ({ value }) => {
    if (!value || value === 0) return null;
    const isPositive = value > 0;
    return (
      <span className={`text-xs font-medium ml-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? '↑' : '↓'} {Math.abs(value).toFixed(1)}%
      </span>
    );
  };

  return (
    <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 shadow-md">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg font-bold text-green-800 flex items-center gap-2">
              🌳 {current_harvest?.name || 'Safra Atual'}
              <HelpIcon tooltip="A safra da seringueira vai de Setembro a Agosto. Este resumo mostra os totais acumulados de toda a safra, não apenas do mês atual. Use 'Comparar' para ver a safra anterior e 'Expandir' para ver o detalhamento mensal." />
            </CardTitle>
            <p className="text-sm text-green-600 mt-1">
              📅 {current_harvest?.period}
            </p>
          </div>
          <div className="flex gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => setShowComparison(!showComparison)}
                    className={`text-sm font-medium px-3 py-1 rounded-full transition-colors ${
                      showComparison 
                        ? 'bg-green-600 text-white' 
                        : 'bg-white text-green-600 border border-green-300 hover:bg-green-50'
                    }`}
                  >
                    📊 {t('ysCompare')}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
                  <p className="text-sm leading-relaxed">Compare os dados da safra atual com a safra anterior para acompanhar a evolução da produção.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => setExpanded(!expanded)}
                    className="text-green-600 hover:text-green-800 text-sm font-medium flex items-center gap-1 px-3 py-1 rounded-full border border-green-300 hover:bg-green-50"
                  >
                    {expanded ? `▲ ${t('ysCollapse')}` : `▼ ${t('ysExpand')}`}
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
                  <p className="text-sm leading-relaxed">Veja o detalhamento mês a mês da safra, incluindo chuva, produção, dias de sangria e aplicações de Ethrel.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Main Stats Grid - Current Harvest */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Total Chuva */}
          <div className="bg-white rounded-lg p-3 border border-blue-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">🌧️</span>
              <span className="text-xs text-gray-600 font-medium">{t('totalRain') || 'Total Chuva'}</span>
            </div>
            <p className="text-xl font-bold text-blue-700">
              {currentSummary.total_chuva_mm?.toFixed(1) || 0} <span className="text-sm font-normal">mm</span>
              {comparison?.total_chuva_mm && <ChangeIndicator value={comparison.total_chuva_mm.change_percent} />}
            </p>
            {showComparison && previousSummary.total_chuva_mm !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                {t('ysPreviousHarvest')}: {previousSummary.total_chuva_mm?.toFixed(1) || 0} mm
              </p>
            )}
          </div>

          {/* Total Produção */}
          <div className="bg-white rounded-lg p-3 border border-green-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">📦</span>
              <span className="text-xs text-gray-600 font-medium">{t('totalProduction') || 'Produção Total'}</span>
            </div>
            <p className="text-xl font-bold text-green-700">
              {currentSummary.total_producao_kg?.toFixed(1) || 0} <span className="text-sm font-normal">kg</span>
              {comparison?.total_producao_kg && <ChangeIndicator value={comparison.total_producao_kg.change_percent} />}
            </p>
            {showComparison && previousSummary.total_producao_kg !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                {t('ysPreviousHarvest')}: {previousSummary.total_producao_kg?.toFixed(1) || 0} kg
              </p>
            )}
          </div>

          {/* kg/planta - NOVO */}
          <div className="bg-white rounded-lg p-3 border border-amber-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">🌱</span>
              <span className="text-xs text-gray-600 font-medium">kg/planta</span>
            </div>
            <p className="text-xl font-bold text-amber-600">
              {currentSummary.kg_por_planta?.toFixed(3) || 0}
              {comparison?.kg_por_planta && <ChangeIndicator value={comparison.kg_por_planta.change_percent} />}
            </p>
            {showComparison && previousSummary.kg_por_planta !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                {t('ysPreviousHarvest')}: {previousSummary.kg_por_planta?.toFixed(3) || 0}
              </p>
            )}
            {/* Linha "efetivo" — só aparece quando há sangradores_alocacao configurado.
                Calcula kg/planta considerando apenas plantas ATIVAS por mês (descontando paralisadas). */}
            {currentSummary.kg_por_planta_efetivo != null && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      className="text-xs font-semibold text-emerald-600 mt-1 cursor-help inline-flex items-center gap-1"
                      data-testid="kg-por-planta-efetivo"
                    >
                      <span>📈 {currentSummary.kg_por_planta_efetivo.toFixed(3)} efetivo</span>
                      {comparison?.kg_por_planta_efetivo && <ChangeIndicator value={comparison.kg_por_planta_efetivo.change_percent} />}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
                    <p className="text-sm leading-relaxed">
                      <strong>kg/planta efetivo</strong> considera apenas as plantas ATIVAS em cada mês (descontando paralisadas por sangradores que saíram). Mostra a produtividade real de quem ficou trabalhando.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <p className="text-xs text-gray-400 mt-1">
              {currentSummary.total_plantas || 0} {t('ysPlants')}
            </p>
            {/* Warning quando a soma da alocação supera o total configurado (indica turnover não normalizado —
                ex: Wesley substituindo Paulo, mas ambos ainda listados). Ajuda o usuário a agir. */}
            {currentSummary.plantas_alocadas_safra != null &&
             currentSummary.total_plantas != null &&
             currentSummary.plantas_alocadas_safra > currentSummary.total_plantas && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p
                      className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1 cursor-help inline-block"
                      data-testid="alocacao-warning"
                    >
                      ⚠️ Alocação inflada: {(currentSummary.plantas_alocadas_safra).toLocaleString('pt-BR')} vs {(currentSummary.total_plantas).toLocaleString('pt-BR')} total
                    </p>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
                    <p className="text-sm leading-relaxed">
                      A soma das plantas alocadas por sangrador ({(currentSummary.plantas_alocadas_safra).toLocaleString('pt-BR')}) supera o total da safra ({(currentSummary.total_plantas).toLocaleString('pt-BR')}).
                      Isso costuma acontecer após substituição de sangrador — o novo entrou mas o antigo ainda está listado.
                      Ajuste em <strong>🗓️ Configurar Safra</strong>: remova o sangrador que saiu ou reduza as plantas dele.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {/* Dias de Sangria */}
          <div className="bg-white rounded-lg p-3 border border-indigo-100 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <img src="/icons/faca_sangria.png" alt="" className="w-5 h-5" />
              <span className="text-xs text-gray-600 font-medium">{t('bleedingDays') || 'Dias Sangria'}</span>
            </div>
            <p className="text-xl font-bold text-indigo-700">
              {currentSummary.total_sangria_dias || 0} <span className="text-sm font-normal">dias</span>
              {comparison?.total_sangria_dias && <ChangeIndicator value={comparison.total_sangria_dias.change_percent} />}
            </p>
            {showComparison && previousSummary.total_sangria_dias !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                {t('ysPreviousHarvest')}: {previousSummary.total_sangria_dias || 0} dias
              </p>
            )}
          </div>

          {/* Total Reposição */}
          <div className="bg-white rounded-lg p-3 border border-purple-100 shadow-sm col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xl">🔄</span>
              <span className="text-xs text-gray-600 font-medium">{t('totalReplacement') || 'Reposição'}</span>
            </div>
            <p className="text-xl font-bold text-purple-700">
              {currentSummary.total_reposicao || 0}
              {comparison?.total_reposicao && <ChangeIndicator value={comparison.total_reposicao.change_percent} />}
            </p>
            {showComparison && previousSummary.total_reposicao !== undefined && (
              <p className="text-xs text-gray-500 mt-1">
                {t('ysPreviousHarvest')}: {previousSummary.total_reposicao || 0}
              </p>
            )}
          </div>
        </div>

        {/* Secondary Stats (Always visible) */}
        <div className="mt-3 flex flex-wrap gap-3 text-sm">
          <div className="bg-orange-50 rounded-full px-3 py-1 border border-orange-200">
            <span className="text-orange-600">⚡ Ethrel: <strong>{currentSummary.total_ethrel_aplicacoes || 0}</strong> {t('ysEthrelApps')}</span>
          </div>
          <div className="bg-purple-50 rounded-full px-3 py-1 border border-purple-200">
            <span className="text-purple-600">🧪 {t('ysFungicideLabel')}: <strong>{currentSummary.total_fungicida_aplicacoes || 0}</strong> {t('ysEthrelApps')}</span>
          </div>
          <div className="bg-red-50 rounded-full px-3 py-1 border border-red-200">
            <span className="text-red-600">❌ {t('ysLosses')}: <strong>{currentSummary.total_sangria_perdida || 0}</strong></span>
          </div>
          <div className="bg-gray-50 rounded-full px-3 py-1 border border-gray-200">
            <span className="text-gray-600">📝 {t('ysRecords')}: <strong>{currentSummary.total_records || 0}</strong></span>
          </div>
          {/* Pill opcional: Equipe / Plantas ativas no mês — só aparece se houver sangradores_alocacao na safra */}
          {teamInfo && teamInfo.plantas_ativas_no_mes != null && (
            (() => {
              const paralisadas = teamInfo.plantas_paralisadas || 0;
              const ativas = teamInfo.plantas_ativas_no_mes || 0;
              const inativos = (teamInfo.sangradores_inativos_no_mes || []).map(s => s.nome).filter(Boolean);
              const hasParalisadas = paralisadas > 0;
              const colorClasses = hasParalisadas
                ? "bg-amber-50 border-amber-200 text-amber-700"
                : "bg-emerald-50 border-emerald-200 text-emerald-700";
              const tooltipText = hasParalisadas
                ? `Plantas ativas no mês: ${ativas} • Paralisadas: ${paralisadas}${inativos.length > 0 ? ` (sem registros: ${inativos.join(', ')})` : ''}. Auto-detectado pela ausência de registros.`
                : `Todos os sangradores alocados (${ativas} plantas) registraram produção neste mês.`;
              return (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className={`rounded-full px-3 py-1 border cursor-help ${colorClasses}`}
                        data-testid="pill-plantas-ativas"
                      >
                        <span>🌳 Plantas ativas: <strong>{ativas.toLocaleString('pt-BR')}</strong>{hasParalisadas && (<span> • <strong>{paralisadas.toLocaleString('pt-BR')}</strong> paralisadas</span>)}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-white text-gray-800 p-3 rounded-lg shadow-xl border border-gray-200">
                      <p className="text-sm leading-relaxed">{tooltipText}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })()
          )}
        </div>

        {/* Harvest Configuration Info */}
        {harvest_config && (
          <div className="mt-3 bg-green-100/50 rounded-lg p-2 text-xs text-green-700">
            ℹ️ {t('ysHarvestPeriod')}: <strong>{harvest_config.inicio_safra_nome}</strong> {t('ysTo')} <strong>{harvest_config.fim_safra_nome}</strong>
          </div>
        )}

        {/* Monthly Breakdown (Expandable) */}
        {expanded && current_harvest?.monthly_breakdown && Object.keys(current_harvest.monthly_breakdown).length > 0 && (
          <div className="mt-4 pt-4 border-t border-green-200">
            <h4 className="text-sm font-semibold text-green-800 mb-3">📅 Detalhamento Mensal - {current_harvest.name}</h4>
            <div className="overflow-x-auto -mx-4 px-4">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-green-100">
                    <th className="p-2 text-left">Mês</th>
                    <th className="p-2 text-center">🌧️ Chuva</th>
                    <th className="p-2 text-center">📦 Produção</th>
                    <th className="p-2 text-center">🩸 Sangria</th>
                    <th className="p-2 text-center">🔄 Reposição</th>
                    <th className="p-2 text-center">⚡ Ethrel</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(current_harvest.monthly_breakdown).map(([month, data]) => {
                    const [yearStr, monthStr] = month.split('-');
                    const monthIndex = parseInt(monthStr);
                    return (
                      <tr key={month} className="border-b border-green-100 hover:bg-green-50">
                        <td className="p-2 font-medium">{monthNames[monthIndex]}/{yearStr.slice(2)}</td>
                        <td className="p-2 text-center">{data.chuva_mm?.toFixed(1) || 0} mm</td>
                        <td className="p-2 text-center">{data.producao_kg?.toFixed(1) || 0} kg</td>
                        <td className="p-2 text-center">{data.sangria_dias || 0} dias</td>
                        <td className="p-2 text-center">{data.reposicao || 0}</td>
                        <td className="p-2 text-center">{data.ethrel_aplicacoes || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Previous Harvest Comparison Table */}
            {showComparison && previous_harvest?.monthly_breakdown && Object.keys(previous_harvest.monthly_breakdown).length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-600 mb-3">📅 Safra Anterior - {previous_harvest.name}</h4>
                <div className="overflow-x-auto -mx-4 px-4">
                  <table className="w-full text-xs opacity-75">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="p-2 text-left">Mês</th>
                        <th className="p-2 text-center">🌧️ Chuva</th>
                        <th className="p-2 text-center">📦 Produção</th>
                        <th className="p-2 text-center">🩸 Sangria</th>
                        <th className="p-2 text-center">🔄 Reposição</th>
                        <th className="p-2 text-center">⚡ Ethrel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(previous_harvest.monthly_breakdown).map(([month, data]) => {
                        const [yearStr, monthStr] = month.split('-');
                        const monthIndex = parseInt(monthStr);
                        return (
                          <tr key={month} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-2 font-medium">{monthNames[monthIndex]}/{yearStr.slice(2)}</td>
                            <td className="p-2 text-center">{data.chuva_mm?.toFixed(1) || 0} mm</td>
                            <td className="p-2 text-center">{data.producao_kg?.toFixed(1) || 0} kg</td>
                            <td className="p-2 text-center">{data.sangria_dias || 0} dias</td>
                            <td className="p-2 text-center">{data.reposicao || 0}</td>
                            <td className="p-2 text-center">{data.ethrel_aplicacoes || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default YearlySummaryCard;
